import { ApolloCache, useMutation, useQuery } from "@apollo/client";
import {
  GENERATE_TASK_CALENDAR_TOKEN_MUTATION,
  REVOKE_TASK_CALENDAR_TOKEN_MUTATION,
  TASK_CALENDAR_SUBSCRIPTION_URL_QUERY,
} from "components/Tasks/schema";
import { useTenant } from "hooks/useTenant";
import { useCallback } from "react";

type UseCalendarSubscriptionUrlOptions = {
  skip?: boolean;
};

/** Keeps the subscription query aligned with the latest generate or revoke result. */
function writeTaskCalendarSubscriptionUrl<T>(
  cache: ApolloCache<T>,
  tenantId: string,
  subscriptionUrl: string | null
) {
  cache.writeQuery({
    query: TASK_CALENDAR_SUBSCRIPTION_URL_QUERY,
    variables: { tenantId },
    data: { taskCalendarSubscriptionUrl: subscriptionUrl },
  });
}

/**
 * Loads and mutates the signed-in user's calendar subscription URL.
 * The tenant-scoped feed includes tasks for every facility the user can access.
 */
export function useCalendarSubscriptionUrl({
  skip = false,
}: UseCalendarSubscriptionUrlOptions = {}) {
  const { tenantId } = useTenant();
  const resolvedTenantId = tenantId ?? "";

  const { data, loading } = useQuery(TASK_CALENDAR_SUBSCRIPTION_URL_QUERY, {
    variables: { tenantId: resolvedTenantId },
    skip: skip || !tenantId,
  });

  const [generateToken, { loading: generating }] = useMutation(
    GENERATE_TASK_CALENDAR_TOKEN_MUTATION
  );
  const [revokeToken, { loading: revoking }] = useMutation(REVOKE_TASK_CALENDAR_TOKEN_MUTATION);

  const subscriptionUrl = data?.taskCalendarSubscriptionUrl ?? null;

  const generateUrl = useCallback(async () => {
    if (!tenantId) {
      throw new Error("Organization is required");
    }

    await generateToken({
      variables: { tenantId },
      update: (cache, { data: mutationData }) => {
        const url = mutationData?.generateTaskCalendarToken;
        if (!url) {
          return;
        }
        writeTaskCalendarSubscriptionUrl(cache, tenantId, url);
      },
    });
  }, [generateToken, tenantId]);

  const revokeUrl = useCallback(async () => {
    if (!tenantId) {
      throw new Error("Organization is required");
    }
    const result = await revokeToken({
      variables: { tenantId },
      optimisticResponse: { revokeTaskCalendarToken: true },
      update: (cache, { data: mutationData }) => {
        if (!mutationData?.revokeTaskCalendarToken) {
          return;
        }
        writeTaskCalendarSubscriptionUrl(cache, tenantId, null);
      },
    });
    return Boolean(result.data?.revokeTaskCalendarToken);
  }, [revokeToken, tenantId]);

  return { subscriptionUrl, loading, generating, revoking, generateUrl, revokeUrl };
}
